import { filesize, type FilesizeOptions, type FilesizeReturn } from "filesize";
import { type MediaEventContent } from "matrix-js-sdk/src/types";

import { _t } from "../languageHandler";

export function downloadLabelForFile(content: MediaEventContent, withSize = true): string {
    let text = _t("action.download");

    if (content.info?.size && withSize) {
        // If we know the size of the file then add it as human-readable string to the end of the link text
        // so that the user knows how big a file they are downloading.
        text += " (" + fileSize(content.info.size, { base: 2, standard: "jedec" }) + ")";
    }
    return text;
}

//OLD (vedi sotto per la nuova implementazione con estensione visibile)
// /**
//  * Extracts a human-readable label for the file attachment to use as
//  * link text.
//  *
//  * @param {MediaEventContent} content The "content" key of the matrix event.
//  * @param {string} fallbackText The fallback text
//  * @param {boolean} withSize Whether to include size information. Default true.
//  * @param {boolean} shortened Ensure the extension of the file name is visible. Default false.
//  * @return {string} the human-readable link text for the attachment.
//  */
// export function presentableTextForFile(
//     content: MediaEventContent,
//     fallbackText = _t("common.attachment"),
//     withSize = true,
//     shortened = false,
// ): string {
//     let text = fallbackText;
//     if (content.filename?.length) {
//         text = content.filename;
//     } else if (content.body?.length) {
//         // The content body should be the name of the file including a
//         // file extension.
//         text = content.body;
//     }

//     // We shorten to 15 characters somewhat arbitrarily, and assume most files
//     // will have a 3 character (plus full stop) extension. The goal is to knock
//     // the label down to 15-25 characters, not perfect accuracy.
//     if (shortened && text.length > 19) {
//         const parts = text.split(".");
//         let fileName = parts
//             .slice(0, parts.length - 1)
//             .join(".")
//             .substring(0, 15);
//         const extension = parts[parts.length - 1];

//         // Trim off any full stops from the file name to avoid a case where we
//         // add an ellipsis that looks really funky.
//         fileName = fileName.replace(/\.*$/g, "");

//         text = `${fileName}...${extension}`;
//     }

//     if (content.info?.size && withSize) {
//         // If we know the size of the file then add it as human readable
//         // string to the end of the link text so that the user knows how
//         // big a file they are downloading.
//         // The content.info also contains a MIME-type but we don't display
//         // it since it is "ugly", users generally aren't aware what it
//         // means and the type of the attachment can usually be inferred
//         // from the file extension.
//         text += " (" + fileSize(content.info.size, { base: 2, standard: "jedec" }) + ")";
//     }
//     return text;
// }

/**
 * wrapper function to set default values for filesize function
 *
 * @param byteCount size of file
 * @param options options to customize the response type or size type conversion e.g. 12kB, 12KB
 * @returns {string | number | any[] | {
 *  value: any;
 *  symbol: any;
 *  exponent: number;
 *  unit: string;}} formatted file size with unit e.g. 12kB, 12KB
 */
export function fileSize<O extends FilesizeOptions>(byteCount: number, options?: O): FilesizeReturn<O> {
    const defaultOption = { base: 2, standard: "jedec", round: 0, roundingMethod: "round", ...options } as O;
    return filesize<O>(byteCount, defaultOption);
}

/**
 * Extracts the file extension from a filename, or an empty string if there is no extension.
 * @param filename The filename to extract the extension from.
 * @returns The file extension, or an empty string if there is no extension.
 */
export function fileExtension(filename: string): string {
    // Find the last dot in the filename
    const dotIdx = filename.lastIndexOf(".");
    // If there is no dot, or the dot is the last character, then there is no extension
    if (dotIdx < 0 || dotIdx >= filename.length - 1) return "";
    const ext = filename.substring(dotIdx + 1);
    // If the extension is unreasonably long, it's likely that the filename doesn't actually have an extension and we should return an empty string instead.
    return ext.length > 5 ? "" : ext;
}

/**
 * Extracts a human-readable label for the file attachment to use as
 * link text, and ensures the file extension is visible if possible.
 */
export function contentFilename(
    // contenuto dell'evento media
    content: MediaEventContent,
    // testo di fallback se non c'è nome file
    fallbackText = _t("common.attachment"),
): string {
    // inizializza il testo con il fallback
    let text = fallbackText;
    // se esiste filename
    if (content.filename?.length) {
        // usa filename
        text = content.filename;
    }
    // altrimenti se esiste body
    else if (content.body?.length) {
        // The content body should be the name of the file including a
        // file extension.
        // Il body dovrebbe essere il nome del file incluso di estensione
        text = content.body;
    }
    return text;
}

/**
 * Extracts a human-readable label for the file attachment to use as
 * link text.
 *
 * @param {MediaEventContent} content The "content" key of the matrix event.
 * @param {string} fallbackText The fallback text
 * @param {boolean} withSize Whether to include size information. Default true.
 * @param {boolean} shortened Ensure the extension of the file name is visible. Default false.
 * @param {number} shortenedMaxLength The maximum length of the file name to show when shortened is true, including extension.
 * @return {string} the human-readable link text for the attachment.
 */
export function presentableTextForFile(
    // contenuto dell'evento media
    content: MediaEventContent,
    // testo di fallback se non c'è nome file
    fallbackText = _t("common.attachment"),
    // se includere la dimensione del file
    withSize = true,
    // se accorciare il nome del file
    shortened = false,
    // max length of the file name to show when shortened is true, including extension (default 25 to allow for 20 character file name + 4 character extension + 1 character for the ellipsis)
    shortenedMaxLength = 25
): string {
    // Prelievo filename dal content
    let text = contentFilename(content, fallbackText);

    // Se shortened è true e il testo è più lungo del massimo consentito,
    // accorcia il nome del file assicurandoti che l'estensione sia visibile
    if (shortened && text.length > shortenedMaxLength) {
        const parts = text.split(".");
        const extension = parts.length > 1 ? parts[parts.length - 1] : "";
        if (extension.length > 0 && extension.length <= 5) {
            // Estensione valida: tronca solo il nome, poi aggiunge ...estensione
            let fileName = parts.slice(0, parts.length - 1).join(".");
            // Calcola la lunghezza massima per il nome: tutto deve stare in shortenedMaxLength
            const maxNameLen = shortenedMaxLength - (3 + extension.length);
            fileName = fileName.substring(0, maxNameLen);
            fileName = fileName.replace(/\.*$/g, "");
            text = `${fileName}...${extension}`;
        } else {
            // Nessuna estensione o troppo lunga: tronca tutto il testo
            text = text.substring(0, shortenedMaxLength - 3) + "...";
        }
    }

    // if (content.info?.size && withSize) {
    // se esiste la dimensione e va mostrata (includo anche 0 byte)
    if (content.info?.size != null && withSize) {
        // If we know the size of the file then add it as human readable
        // string to the end of the link text so that the user knows how
        // big a file they are downloading.
        // The content.info also contains a MIME-type but we don't display
        // it since it is "ugly", users generally aren't aware what it
        // means and the type of the attachment can usually be inferred
        // from the file extension.
        // Se conosciamo la dimensione del file, aggiungiamola in formato leggibile alla fine del testo del link così l'utente sa quanto è grande il file che sta scaricando.
        // content.info contiene anche il MIME-type ma non lo mostriamo perché è "brutto", gli utenti generalmente non sanno cosa significhi e il tipo di allegato si può intuire dall'estensione.
        // aggiungi la dimensione
        text += " (" + fileSize(content.info.size, { base: 2, standard: "jedec" }) + ")";
    }
    // restituisci il testo finale
    return text;
}